import React from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Button, Card, Icon } from '@node-space/ui-components'
import type { GroupedLimit, Limit } from 'types/paymentLimits'
import { formatNumberWithCommas } from 'utils/utils'
import { formatPaymentMethodLabel, LIMIT_TYPE_LABELS } from '../utils'

interface PaymentLimitCardProps {
  groupedLimit: GroupedLimit
  onEditLimit?: (limit: Limit) => void
  onViewDetails?: (limit: Limit) => void
}

export const PaymentLimitCard = ({
  groupedLimit,
  onEditLimit,
  onViewDetails,
}: PaymentLimitCardProps) => {
  const { t } = useTranslation()
  const { method, currency, transactionLimit, dailyLimit } = groupedLimit

  const handleLimitRowClick = (limit: Limit | undefined) => {
    if (limit) {
      onViewDetails?.(limit)
    }
  }

  const handleEditClick = (limit: Limit) => {
    onEditLimit?.(limit)
  }

  return (
    <Card variant="static" emphasis full className="py-6">
      <Box flexCol>
        <h3 className="text-h3 text-foreground-prominent">{formatPaymentMethodLabel(method)}</h3>

        <Box flex align="center" gap={8} className="mt-1 mb-4">
          <Avatar name={currency} type="currency" size="small" />
          <Box className="text-body-strong text-foreground-prominent">{currency}</Box>
        </Box>

        {transactionLimit && (
          <Box
            flex
            justify="between"
            align="center"
            className="pt-4 pb-2 cursor-pointer"
            onClick={() => handleLimitRowClick(transactionLimit)}
          >
            <Box className="text-body-regular-small text-foreground-subdued">
              {t(LIMIT_TYPE_LABELS[transactionLimit.limitType])}
            </Box>
            <Box flex align="center" gap={8}>
              <Box className="text-body-regular-small text-foreground-prominent">
                {formatNumberWithCommas(transactionLimit.amount)}
              </Box>
              <Icon name="CaretRight" />
            </Box>
          </Box>
        )}

        {dailyLimit && (
          <Box
            flex
            justify="between"
            align="center"
            className="pt-2 pb-2 cursor-pointer"
            onClick={() => handleLimitRowClick(dailyLimit)}
          >
            <Box className="text-body-regular-small text-foreground-subdued">
              {t(LIMIT_TYPE_LABELS[dailyLimit.limitType])}
            </Box>
            <Box flex align="center" gap={8}>
              <Box className="text-body-regular-small text-foreground-prominent">
                {formatNumberWithCommas(dailyLimit.amount)}
              </Box>
              <Icon name="CaretRight" />
            </Box>
          </Box>
        )}

        <Box flex justify="center" gap={16} className="pt-8">
          {transactionLimit && (
            <Button
              variant="ghost"
              noPadding
              onClick={() => handleEditClick(transactionLimit)}
              iconElement={<Icon name="SimplePencilCustom" />}
            >
              {t('paymentLimits.editTransactionLimit')}
            </Button>
          )}
          {dailyLimit && (
            <Button
              variant="ghost"
              noPadding
              onClick={() => handleEditClick(dailyLimit)}
              iconElement={<Icon name="SimplePencilCustom" />}
            >
              {t('paymentLimits.editDailyLimit')}
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  )
}
